/**
 * Default Merchant Knowledge Base
 * Pre-configured patterns for common merchants to enable intelligent categorization
 */

export interface MerchantKnowledge {
  pattern: string; // Regex pattern or exact match
  merchant_name?: string;
  general_category: string;
  federal_category?: string | null;
  oregon_category?: string | null;
  confidence: number;
  notes?: string;
}

export const DEFAULT_MERCHANT_KNOWLEDGE: MerchantKnowledge[] = [
  // ========================================
  // TELECOMMUNICATIONS
  // ========================================
  {
    pattern: "VERIZON",
    merchant_name: "Verizon",
    general_category: "Utilities",
    federal_category: "home_office",
    oregon_category: "utilities_telecom",
    confidence: 0.9,
    notes: "Internet & Cell Phone provider",
  },
  {
    pattern: "AT&T|ATT WIRELESS|ATT INTERNET",
    merchant_name: "AT&T",
    general_category: "Mobile Phone",
    federal_category: "home_office",
    oregon_category: "utilities_telecom",
    confidence: 0.9,
  },
  {
    pattern: "T-MOBILE|TMOBILE",
    merchant_name: "T-Mobile",
    general_category: "Mobile Phone",
    federal_category: "home_office",
    oregon_category: "utilities_telecom",
    confidence: 0.9,
  },
  {
    pattern: "COMCAST|XFINITY",
    merchant_name: "Comcast/Xfinity",
    general_category: "Internet Service",
    federal_category: "home_office",
    oregon_category: "utilities_telecom",
    confidence: 0.9,
  },
  {
    pattern: "CENTURYLINK|CENTURY LINK",
    merchant_name: "CenturyLink",
    general_category: "Internet Service",
    federal_category: "home_office",
    oregon_category: "utilities_telecom",
    confidence: 0.9,
  },

  // ========================================
  // UTILITIES
  // ========================================
  {
    pattern: "PACIFIC POWER|PACIFICORP",
    merchant_name: "Pacific Power",
    general_category: "Electric",
    federal_category: "home_office",
    oregon_category: "utilities_electric",
    confidence: 0.95,
  },
  {
    pattern: "PORTLAND GENERAL|PGE ",
    merchant_name: "Portland General Electric",
    general_category: "Electric",
    federal_category: "home_office",
    oregon_category: "utilities_electric",
    confidence: 0.95,
  },
  {
    pattern: "NW NATURAL|NORTHWEST NATURAL",
    merchant_name: "NW Natural Gas",
    general_category: "Gas",
    federal_category: "home_office",
    oregon_category: "utilities_gas",
    confidence: 0.95,
  },
  {
    pattern: "PORTLAND WATER",
    merchant_name: "Portland Water Bureau",
    general_category: "Water",
    federal_category: "home_office",
    oregon_category: "utilities_water",
    confidence: 0.95,
  },

  // ========================================
  // STREAMING & SOFTWARE
  // ========================================
  {
    pattern: "NETFLIX",
    merchant_name: "Netflix",
    general_category: "Streaming Services",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "HULU",
    merchant_name: "Hulu",
    general_category: "Streaming Services",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "DISNEY\\+|DISNEY PLUS",
    merchant_name: "Disney+",
    general_category: "Streaming Services",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "SPOTIFY",
    merchant_name: "Spotify",
    general_category: "Music",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "APPLE MUSIC|ITUNES",
    merchant_name: "Apple Music",
    general_category: "Music",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "ADOBE",
    merchant_name: "Adobe",
    general_category: "Software",
    federal_category: "software_subscriptions",
    oregon_category: "business_software",
    confidence: 0.85,
  },
  {
    pattern: "MICROSOFT|OFFICE 365",
    merchant_name: "Microsoft",
    general_category: "Software",
    federal_category: "software_subscriptions",
    oregon_category: "business_software",
    confidence: 0.85,
  },
  {
    pattern: "GITHUB",
    merchant_name: "GitHub",
    general_category: "Software",
    federal_category: "software_subscriptions",
    oregon_category: "business_software",
    confidence: 0.9,
  },

  // ========================================
  // GAS STATIONS
  // ========================================
  {
    pattern: "CHEVRON",
    merchant_name: "Chevron",
    general_category: "Gas Stations",
    federal_category: "vehicle_expenses",
    oregon_category: "transportation",
    confidence: 0.8,
  },
  {
    pattern: "SHELL ",
    merchant_name: "Shell",
    general_category: "Gas Stations",
    federal_category: "vehicle_expenses",
    oregon_category: "transportation",
    confidence: 0.8,
  },
  {
    pattern: "76 STATION|SEVENTY SIX",
    merchant_name: "76",
    general_category: "Gas Stations",
    federal_category: "vehicle_expenses",
    oregon_category: "transportation",
    confidence: 0.8,
  },
  {
    pattern: "ARCO",
    merchant_name: "ARCO",
    general_category: "Gas Stations",
    federal_category: "vehicle_expenses",
    oregon_category: "transportation",
    confidence: 0.8,
  },
  {
    pattern: "COSTCO GAS",
    merchant_name: "Costco Gas",
    general_category: "Gas Stations",
    federal_category: "vehicle_expenses",
    oregon_category: "transportation",
    confidence: 0.85,
  },

  // ========================================
  // GROCERIES
  // ========================================
  {
    pattern: "SAFEWAY",
    merchant_name: "Safeway",
    general_category: "Groceries",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "FRED MEYER",
    merchant_name: "Fred Meyer",
    general_category: "Groceries",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },
  {
    pattern: "WHOLE FOODS",
    merchant_name: "Whole Foods",
    general_category: "Groceries",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "TRADER JOE",
    merchant_name: "Trader Joe's",
    general_category: "Groceries",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "NEW SEASONS",
    merchant_name: "New Seasons Market",
    general_category: "Groceries",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "COSTCO WHOLESALE|COSTCO WHSE",
    merchant_name: "Costco",
    general_category: "Groceries",
    federal_category: null,
    oregon_category: null,
    confidence: 0.85,
  },
  {
    pattern: "WINCO",
    merchant_name: "WinCo Foods",
    general_category: "Groceries",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },

  // ========================================
  // RESTAURANTS & FOOD
  // ========================================
  {
    pattern: "STARBUCKS",
    merchant_name: "Starbucks",
    general_category: "Coffee Shops",
    federal_category: "meals_entertainment",
    oregon_category: null,
    confidence: 0.9,
  },
  {
    pattern: "DUTCH BROS",
    merchant_name: "Dutch Bros",
    general_category: "Coffee Shops",
    federal_category: "meals_entertainment",
    oregon_category: null,
    confidence: 0.9,
  },
  {
    pattern: "MCDONALD'S|MCDONALDS",
    merchant_name: "McDonald's",
    general_category: "Fast Food",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "CHIPOTLE",
    merchant_name: "Chipotle",
    general_category: "Fast Food",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "DOORDASH|DOOR DASH",
    merchant_name: "DoorDash",
    general_category: "Food and Drink",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },
  {
    pattern: "UBER EATS|UBEREATS",
    merchant_name: "Uber Eats",
    general_category: "Food and Drink",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },
  {
    pattern: "GRUBHUB",
    merchant_name: "Grubhub",
    general_category: "Food and Drink",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },

  // ========================================
  // TRANSPORTATION
  // ========================================
  {
    pattern: "UBER ",
    merchant_name: "Uber",
    general_category: "Rideshare",
    federal_category: "travel_expenses",
    oregon_category: "transportation",
    confidence: 0.85,
  },
  {
    pattern: "LYFT ",
    merchant_name: "Lyft",
    general_category: "Rideshare",
    federal_category: "travel_expenses",
    oregon_category: "transportation",
    confidence: 0.85,
  },
  {
    pattern: "TRIMET",
    merchant_name: "TriMet",
    general_category: "Public Transportation",
    federal_category: "travel_expenses",
    oregon_category: "transportation",
    confidence: 0.95,
  },

  // ========================================
  // AIRLINES
  // ========================================
  {
    pattern: "ALASKA AIR",
    merchant_name: "Alaska Airlines",
    general_category: "Airlines",
    federal_category: "travel_expenses",
    oregon_category: "travel",
    confidence: 0.85,
  },
  {
    pattern: "UNITED AIR",
    merchant_name: "United Airlines",
    general_category: "Airlines",
    federal_category: "travel_expenses",
    oregon_category: "travel",
    confidence: 0.85,
  },
  {
    pattern: "SOUTHWEST AIR",
    merchant_name: "Southwest Airlines",
    general_category: "Airlines",
    federal_category: "travel_expenses",
    oregon_category: "travel",
    confidence: 0.85,
  },
  {
    pattern: "DELTA AIR",
    merchant_name: "Delta Airlines",
    general_category: "Airlines",
    federal_category: "travel_expenses",
    oregon_category: "travel",
    confidence: 0.85,
  },

  // ========================================
  // INSURANCE
  // ========================================
  {
    pattern: "STATE FARM",
    merchant_name: "State Farm",
    general_category: "Insurance",
    federal_category: "insurance",
    oregon_category: "insurance",
    confidence: 0.9,
  },
  {
    pattern: "GEICO",
    merchant_name: "GEICO",
    general_category: "Auto Insurance",
    federal_category: "vehicle_expenses",
    oregon_category: "insurance",
    confidence: 0.9,
  },
  {
    pattern: "PROGRESSIVE",
    merchant_name: "Progressive",
    general_category: "Auto Insurance",
    federal_category: "vehicle_expenses",
    oregon_category: "insurance",
    confidence: 0.9,
  },
  {
    pattern: "ALLSTATE",
    merchant_name: "Allstate",
    general_category: "Insurance",
    federal_category: "insurance",
    oregon_category: "insurance",
    confidence: 0.9,
  },

  // ========================================
  // HEALTHCARE
  // ========================================
  {
    pattern: "KAISER PERMANENTE",
    merchant_name: "Kaiser Permanente",
    general_category: "Medical",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.95,
  },
  {
    pattern: "PROVIDENCE",
    merchant_name: "Providence Health",
    general_category: "Medical",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.95,
  },
  {
    pattern: "OHSU",
    merchant_name: "OHSU",
    general_category: "Medical",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.95,
  },
  {
    pattern: "CVS PHARMACY|CVS/PHARMACY",
    merchant_name: "CVS Pharmacy",
    general_category: "Pharmacy",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.9,
  },
  {
    pattern: "WALGREENS",
    merchant_name: "Walgreens",
    general_category: "Pharmacy",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.9,
  },
  {
    pattern: "RITE AID",
    merchant_name: "Rite Aid",
    general_category: "Pharmacy",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.9,
  },

  // ========================================
  // RETAIL
  // ========================================
  {
    pattern: "AMAZON",
    merchant_name: "Amazon",
    general_category: "Online Shopping",
    federal_category: null,
    oregon_category: null,
    confidence: 0.7,
  },
  {
    pattern: "TARGET",
    merchant_name: "Target",
    general_category: "Department Stores",
    federal_category: null,
    oregon_category: null,
    confidence: 0.8,
  },
  {
    pattern: "WALMART",
    merchant_name: "Walmart",
    general_category: "Department Stores",
    federal_category: null,
    oregon_category: null,
    confidence: 0.8,
  },
  {
    pattern: "HOME DEPOT",
    merchant_name: "Home Depot",
    general_category: "Home Improvement",
    federal_category: "home_office",
    oregon_category: null,
    confidence: 0.75,
  },
  {
    pattern: "LOWES",
    merchant_name: "Lowe's",
    general_category: "Home Improvement",
    federal_category: "home_office",
    oregon_category: null,
    confidence: 0.75,
  },

  // ========================================
  // FINANCIAL
  // ========================================
  {
    pattern: "CHASE CREDIT|CHASE CARD",
    merchant_name: "Chase",
    general_category: "Credit Cards",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "BANK OF AMERICA|BOFA",
    merchant_name: "Bank of America",
    general_category: "Banking Fees",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "WELLS FARGO",
    merchant_name: "Wells Fargo",
    general_category: "Banking Fees",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },

  // ========================================
  // GOVERNMENT
  // ========================================
  {
    pattern: "IRS|TREASURY",
    merchant_name: "IRS",
    general_category: "Taxes",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "OREGON DEPT|OR DEPT REV",
    merchant_name: "Oregon Department of Revenue",
    general_category: "Taxes",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  {
    pattern: "DMV|DEPARTMENT OF MOTOR",
    merchant_name: "DMV",
    general_category: "DMV",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
];

/**
 * Find matching merchant pattern
 */
export function findMerchantMatch(
  merchantName: string | undefined,
  transactionName: string
): MerchantKnowledge | null {
  if (!merchantName && !transactionName) return null;

  const searchString = (merchantName || transactionName).toUpperCase();

  for (const knowledge of DEFAULT_MERCHANT_KNOWLEDGE) {
    const regex = new RegExp(knowledge.pattern, "i");
    if (regex.test(searchString)) {
      return knowledge;
    }
  }

  return null;
}

/**
 * Get confidence level description
 */
export function getConfidenceDescription(confidence: number): string {
  if (confidence >= 0.9) return "Very High";
  if (confidence >= 0.8) return "High";
  if (confidence >= 0.7) return "Good";
  if (confidence >= 0.6) return "Moderate";
  if (confidence >= 0.5) return "Low";
  return "Very Low";
}

/**
 * Normalize merchant name for pattern matching
 */
export function normalizeMerchantName(name: string): string {
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, "") // Remove special characters
    .replace(/\s+/g, " ") // Normalize spaces
    .trim();
}
